/**
 * EmailArchePage
 *
 * Route wrapper for the email arche (external-client rebuild). The OAuth connect
 * callback renders standalone (popup); everything else renders inside
 * ArcheLayout. v1 surfaces the functional mailbox + account connections; the
 * legacy rich pages (settings/contacts/search/screener) are re-layered in
 * Phase 6 onto the new Vault model.
 */

import { ReactNode } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ArcheLayout } from '../../../components/ArcheLayout';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { MailboxPage } from './MailboxPage';
import { ConnectionsPage } from './ConnectionsPage';
import { ConnectCallbackPage } from './ConnectCallbackPage';

/**
 * Shell that gives every email sub-route the standard arche header (name, star,
 * info, sidebar/suggested toggle, focus-mode toggle). Rendered ABOVE the inner
 * routes so it persists across MailboxPage/ConnectionsPage and across
 * MailboxPage's own loading/empty/error early-returns. The header self-hides in
 * focus mode (see ArcheHeader + ChromeContext).
 *
 * Focus mode: the platform's bottom-right floating restore controls are
 * suppressed for email (ArcheLayout `hideFloatingRestore`); MailboxPage instead
 * renders Back-to-Fias + Show-headers INSIDE its left sidebar (above the account
 * selector) so restoring chrome doesn't shift the message list down. The
 * Connections page keeps its "Back to inbox" link as the route back to that
 * restore affordance.
 */
export function EmailShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col h-full min-h-0">
      <ArcheHeader archeId="arche_email" title="Email" />
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

export function EmailArchePage() {
  return (
    <Routes>
      {/* OAuth popup callback — standalone, no ArcheLayout chrome. */}
      <Route path="/connect/callback" element={<ConnectCallbackPage />} />
      <Route
        path="/*"
        element={
          <ArcheLayout hideFloatingRestore>
            <EmailShell>
              <Routes>
                <Route path="/connections" element={<ConnectionsPage />} />
                <Route path="/folder/:folderId" element={<MailboxPage />} />
                <Route path="/*" element={<MailboxPage />} />
              </Routes>
            </EmailShell>
          </ArcheLayout>
        }
      />
    </Routes>
  );
}
